import React, { memo, useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useDispatch, useSelector } from 'react-redux'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { BasicModal } from 'src/components/shared/modal/BasicModal'
import {
  ArchiveCandidate,
  FetchCandidateCounts,
  FetchCandidates,
  FetchOpportunities,
  UnarchiveCandidate,
} from 'src/constants/actionTypes'
import { getCandidatePoolColumns } from 'src/constants/candidatePool/candidatePoolGrid'
import {
  CANDIDATE_DEFAULT_PAGE_SIZE,
  CANDIDATE_PAGE_SIZE_OPTIONS,
  CANDIDATE_SORTABLE_COLUMNS,
  CANDIDATE_TAB_TITLES,
} from 'src/constants/candidatePool/constants'
import { OpportunityCandidateStatus } from 'src/constants/opportunity/enums'
import { VehicleType, vehicleNamesMap } from 'src/constants/vehicle'
import {
  CandidateFilters,
  Opportunity,
  OpportunityCandidate,
  OpportunityCandidateCounts,
} from 'src/interfaces/opportunity'
import { getMarkets } from 'src/selectors/market'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { addS } from 'src/utils/baseUtils'
import { showToast } from 'src/utils/toast'
import { TableTab, TableTabs } from 'src/components/shared/table/table-tabs/TableTabs'
import { TableSearch } from 'src/components/shared/table/TableSearch'
import { TableSelect } from 'src/components/shared/table/TableSelect'
import { TableMarketSelect } from 'src/components/shared/table/TableMarketSelect'
import { GSIcon } from 'src/components/shared/GSIcon'
import { colors } from 'src/theme'
import { CircleCancelMajor, CustomerPlusMajor, InviteMinor } from '@shopify/polaris-icons'
import { CandidateActionMenu } from './CandidateActionMenu'
import { CandidateBatchActionButton } from './CandidateBatchActionButton'
import { CandidatePoolTableContext } from './CandidatePoolTable.context'
import { EmptyCandidates } from './EmptyCandidates'
import { OfferCandidatesForm } from './offer-candidates-form/OfferCandidatesForm'

const DEFAULT_SORT: GridSortModel = [{ field: 'createdAt', sort: 'desc' }]

const TAB_ICONS = {
  [OpportunityCandidateStatus.New]: CustomerPlusMajor,
  [OpportunityCandidateStatus.Offered]: InviteMinor,
  [OpportunityCandidateStatus.Archived]: CircleCancelMajor,
}

const getRowId = (row: OpportunityCandidate) => {
  return row.id
}

/**
 * Collapses the shared multi-select's value array down to one, for filters that only ever narrow to a single value.
 * The array arrives with the previous value still in it, so take whatever is not the current one — that is the
 * option just ticked. Unticking the active option yields an empty array and clears the filter.
 */
const pickOne = (values: Array<string>, current?: string): string => {
  return (
    values.find((value) => {
      return value !== current
    }) || ''
  )
}

const CandidatePoolComponent = () => {
  const dispatch = useDispatch()

  const markets = useSelector(getMarkets)

  const [selectedTab, setSelectedTab] = useState<OpportunityCandidateStatus>(
    OpportunityCandidateStatus.New,
  )
  const [filters, setFilters] = useState<CandidateFilters>({})
  const [searchInput, setSearchInput] = useState<string>('')
  const [candidates, setCandidates] = useState<Array<OpportunityCandidate>>([])
  const [opportunities, setOpportunities] = useState<Array<Opportunity>>([])
  const [counts, setCounts] = useState<OpportunityCandidateCounts>({
    new: 0,
    offered: 0,
    archived: 0,
  })
  const [total, setTotal] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
  const [batchAnchor, setBatchAnchor] = useState<null | HTMLElement>(null)
  const [rowAnchor, setRowAnchor] = useState<null | HTMLElement>(null)
  const [candidateToAction, setCandidateToAction] = useState<OpportunityCandidate | null>(null)
  /**
   * The offer modal is reachable from a row kebab and from the batch menu, so it works off an explicit scope rather
   * than reading the checkbox selection directly. Cleared on close, so a row-scoped offer cannot leak into the next
   * batch one.
   */
  const [offerScope, setOfferScope] = useState<Array<OpportunityCandidate> | null>(null)
  // Filters, tab, sort and paging all refetch. Without this, a slower earlier request can resolve last and
  // overwrite the newer result with stale rows.
  const latestCandidatesRequest = useRef<number>(0)
  const latestCountsRequest = useRef<number>(0)

  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: CANDIDATE_DEFAULT_PAGE_SIZE,
  })

  const fetchCandidates = useCallback(async () => {
    latestCandidatesRequest.current += 1
    const requestId = latestCandidatesRequest.current

    setIsLoading(true)

    try {
      const result: { data: OpportunityCandidate[]; total: number } = await createAsyncAction(
        dispatch,
        FetchCandidates.request({
          ...filters,
          status: selectedTab,
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
          sortColumn: CANDIDATE_SORTABLE_COLUMNS[sortModel[0]?.field] || 'createdAt',
          sortDirection: sortModel[0]?.sort === 'asc' ? 'ASC' : 'DESC',
        }),
      )

      if (requestId !== latestCandidatesRequest.current) {
        return
      }

      setCandidates(result?.data || [])
      setTotal(result?.total || 0)
    } catch (err: any) {
      if (requestId !== latestCandidatesRequest.current) {
        return
      }

      handleApiError(err)
    } finally {
      if (requestId === latestCandidatesRequest.current) {
        setIsLoading(false)
      }
    }
  }, [dispatch, filters, selectedTab, paginationModel, sortModel])

  const fetchCounts = useCallback(async () => {
    latestCountsRequest.current += 1
    const requestId = latestCountsRequest.current

    try {
      const result: OpportunityCandidateCounts = await createAsyncAction(
        dispatch,
        // Status is deliberately not sent: the counts drive the tab badges, so they must span every status.
        FetchCandidateCounts.request({ ...filters, status: undefined }),
      )

      if (requestId !== latestCountsRequest.current) {
        return
      }

      setCounts(result || { new: 0, offered: 0, archived: 0 })
    } catch (err: any) {
      if (requestId === latestCountsRequest.current) {
        handleApiError(err)
      }
    }
  }, [dispatch, filters])

  const fetchOpportunities = useCallback(async () => {
    try {
      const result: { data: Opportunity[] } = await createAsyncAction(
        dispatch,
        FetchOpportunities.request(),
      )

      setOpportunities(result?.data || [])
    } catch {
      // The opportunity filter is a convenience; the pool is still usable without it.
      setOpportunities([])
    }
  }, [dispatch])

  useEffect(() => {
    fetchOpportunities()
  }, [fetchOpportunities])

  useEffect(() => {
    fetchCandidates()
  }, [fetchCandidates])

  useEffect(() => {
    fetchCounts()
  }, [fetchCounts])

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput || undefined }))
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }, 350)

    return () => {
      clearTimeout(timer)
    }
  }, [searchInput])

  const handleChangeFilter = useCallback((key: keyof CandidateFilters, value?: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }))
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    // Selections refer to rows that may no longer be visible once the filter changes.
    setSelectionModel([])
  }, [])

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model.length ? model : DEFAULT_SORT)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setSelectionModel([])
  }, [])

  const handleSelectTab = useCallback((tab: OpportunityCandidateStatus) => {
    setSelectedTab(tab)
    setSelectionModel([])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [])

  const refresh = useCallback(() => {
    setSelectionModel([])
    fetchCandidates()
    fetchCounts()
  }, [fetchCandidates, fetchCounts])

  const handleCloseBatchMenu = useCallback(() => {
    setBatchAnchor(null)
  }, [])

  const handleOpenBatchMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setBatchAnchor(event.currentTarget)
  }, [])

  const handleCloseRowMenu = useCallback(() => {
    setRowAnchor(null)
  }, [])

  const handleClearRowMenu = useCallback(() => {
    setRowAnchor(null)
    setCandidateToAction(null)
  }, [])

  const handleOpenRowMenu = useCallback(
    (event: MouseEvent<HTMLButtonElement>, candidate: OpportunityCandidate) => {
      setRowAnchor(event.currentTarget)
      setCandidateToAction(candidate)
    },
    [],
  )

  const handleCloseOffer = useCallback(() => {
    setOfferScope(null)
    setCandidateToAction(null)
  }, [])

  // Row and batch share one implementation, so the two paths cannot drift.
  const handleArchive = useCallback(
    async (candidateIds: Array<string>) => {
      if (!candidateIds.length) {
        return
      }

      try {
        await Promise.all(
          candidateIds.map((candidateId) =>
            createAsyncAction(dispatch, ArchiveCandidate.request({ candidateId })),
          ),
        )

        showToast(
          `${candidateIds.length} ${addS(
            candidateIds.length,
            'candidate',
          )} archived. Still searchable, still costing nothing.`,
          { variant: 'success' },
        )

        refresh()
      } catch (err: any) {
        handleApiError(err)
      }
    },
    [dispatch, refresh],
  )

  const handleUnarchive = useCallback(
    async (candidateIds: Array<string>) => {
      if (!candidateIds.length) {
        return
      }

      try {
        await Promise.all(
          candidateIds.map((candidateId) =>
            createAsyncAction(dispatch, UnarchiveCandidate.request({ candidateId })),
          ),
        )

        showToast(`${candidateIds.length} moved back to New`, { variant: 'success' })

        refresh()
      } catch (err: any) {
        handleApiError(err)
      }
    },
    [dispatch, refresh],
  )

  const tabs = useMemo<Array<TableTab<OpportunityCandidateStatus>>>(() => {
    return Object.values(OpportunityCandidateStatus).map((status) => {
      const Icon = TAB_ICONS[status]

      return {
        value: status,
        title: CANDIDATE_TAB_TITLES[status],
        subtitle: `${counts[status] ?? 0} ${addS(counts[status] ?? 0, 'candidate')}`,
        dataTestId: `candidate-pool-tab-${status}`,
        renderIcon: (selected: boolean) => (
          <Icon
            width={20}
            height={20}
            fill={selected ? colors.PRIMARY_PURPLE_600 : colors.GRAY_BLACK}
          />
        ),
      }
    })
  }, [counts])

  const selectedCandidates = useMemo(() => {
    return candidates.filter((candidate) => {
      return selectionModel.includes(candidate.id)
    })
  }, [candidates, selectionModel])

  const openOfferForCandidates = useCallback((scope: Array<OpportunityCandidate>) => {
    setOfferScope(scope)
  }, [])

  const columns = useMemo(() => {
    return getCandidatePoolColumns(selectedTab)
  }, [selectedTab])

  const isSelectable = selectedTab !== OpportunityCandidateStatus.Offered

  const vehicleTypeOptions = useMemo(() => {
    return Object.values(VehicleType).map((vehicleType) => ({
      value: vehicleType,
      label: vehicleNamesMap[vehicleType],
      component: <Typography variant="subtitle1">{vehicleNamesMap[vehicleType]}</Typography>,
    }))
  }, [])

  const opportunityOptions = useMemo(() => {
    return opportunities.map((opportunity) => ({
      value: opportunity.id,
      label: opportunity.title,
      component: <Typography variant="subtitle1">{opportunity.title}</Typography>,
    }))
  }, [opportunities])

  // The trigger label doubles as the active-filter indicator, so it names the selection rather than the filter.
  const selectedMarketTitle = useMemo(() => {
    const market = markets?.find((item: any) => {
      return item.id === filters.marketId
    })

    return market?.title || 'All markets'
  }, [markets, filters.marketId])

  const selectedVehicleTypeTitle = filters.vehicleType
    ? vehicleNamesMap[filters.vehicleType]
    : 'All vehicle types'

  const selectedOpportunityTitle = useMemo(() => {
    return (
      opportunities.find((opportunity) => {
        return opportunity.id === filters.opportunityId
      })?.title || 'All opportunities'
    )
  }, [opportunities, filters.opportunityId])

  // The tab itself is not a filter: an empty Archived tab is genuinely empty, not filtered to nothing.
  const hasActiveFilters = Boolean(
    filters.marketId || filters.vehicleType || filters.opportunityId || filters.search,
  )

  const renderEmpty = useCallback(() => {
    return <EmptyCandidates hasActiveFilters={hasActiveFilters} />
  }, [hasActiveFilters])

  const tableContext = useMemo(() => {
    return {
      refreshCandidates: refresh,
      selectedTab,
      selectedCandidates,
      openOfferForCandidates,
      archiveCandidates: handleArchive,
      unarchiveCandidates: handleUnarchive,
      rowAnchor,
      candidateToAction,
      openCandidateItemAction: handleOpenRowMenu,
      closeCandidateItemAction: handleCloseRowMenu,
      clearCandidateToAction: handleClearRowMenu,
      batchAnchor,
      openBatchAction: handleOpenBatchMenu,
      closeBatchAction: handleCloseBatchMenu,
    }
  }, [
    refresh,
    selectedTab,
    selectedCandidates,
    openOfferForCandidates,
    handleArchive,
    handleUnarchive,
    rowAnchor,
    candidateToAction,
    handleOpenRowMenu,
    handleCloseRowMenu,
    handleClearRowMenu,
    batchAnchor,
    handleOpenBatchMenu,
    handleCloseBatchMenu,
  ])

  return (
    <CandidatePoolTableContext.Provider value={tableContext}>
      <div data-testid="candidate-pool-page">
        <div css={tw`mb-6`}>
          <Typography variant="h2" css={tw`mb-1`}>
            Candidate pool
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`max-w-[62ch]`}>
            Everyone who applied through an opportunity link. Sort and filter on what they told you,
            then offer the ones you want in onboarding. Candidates you never offer sit here at no
            cost — no background check, MVR, or drug screen runs until you make an offer.
          </Typography>
        </div>

        <TableTabs tabs={tabs} selectedValue={selectedTab} onClick={handleSelectTab} />

        <div
          css={tw`flex flex-row items-center justify-between gap-4 bg-white px-4 py-3 border-0 border-b border-t border-solid border-[#EDEDED]`}
        >
          <div css={tw`flex flex-row flex-wrap items-center gap-4`}>
            <TableSearch
              searchQuery={searchInput}
              onChangeSearchQuery={setSearchInput}
              inputDataTestId="candidate-pool-search"
              clearButtonDataTestId="candidate-pool-search-clear-btn"
            />
            {/*
            TableSelect rather than a bare Select: filters across the dashboard are an ActionButton trigger plus a
            menu. Each of these narrows to one value today — a candidate has exactly one market, one vehicle type
            and one opportunity — so the shared multi-select is driven as a single-select from here rather than
            adding a mode to it. `pickOne` keeps the most recently ticked option and drops the previous one, so
            ticking a second replaces rather than appends, and unticking clears.
          */}
            <TableMarketSelect
              selectedMarketIds={filters.marketId ? [filters.marketId] : []}
              setSelectedMarketIds={(values) =>
                handleChangeFilter('marketId', pickOne(values, filters.marketId))
              }
              title={selectedMarketTitle}
              triggerDataTestId="candidate-pool-market-filter"
            />
            <TableSelect
              searchable
              icon={<GSIcon iconName="gs_tag" />}
              title={selectedVehicleTypeTitle}
              options={vehicleTypeOptions}
              selectedValues={filters.vehicleType ? [filters.vehicleType] : []}
              onSelect={(values) =>
                handleChangeFilter('vehicleType', pickOne(values, filters.vehicleType))
              }
              triggerDataTestId="candidate-pool-vehicle-filter"
            />
            <TableSelect
              searchable
              menuWidth={258}
              icon={<GSIcon iconName="gs_bookmark" />}
              title={selectedOpportunityTitle}
              options={opportunityOptions}
              selectedValues={filters.opportunityId ? [filters.opportunityId] : []}
              onSelect={(values) =>
                handleChangeFilter('opportunityId', pickOne(values, filters.opportunityId))
              }
              triggerDataTestId="candidate-pool-opportunity-filter"
            />
          </div>
          {isSelectable && <CandidateBatchActionButton />}
        </div>

        <GridTable
          disableColumnFilter
          rows={candidates}
          columns={columns}
          getRowId={getRowId}
          isLoading={isLoading}
          EmptyElement={renderEmpty}
          checkboxSelection={isSelectable}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          rowCount={total}
          pageSizeOptions={CANDIDATE_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          tableDataTestId="candidate-pool-table"
          rowDataTestIdPrefix="candidate-pool"
        />

        <CandidateActionMenu />

        <BasicModal isOpen={Boolean(offerScope)} onClose={handleCloseOffer}>
          {offerScope ? (
            <OfferCandidatesForm
              candidates={offerScope}
              onClose={handleCloseOffer}
              onOffered={refresh}
            />
          ) : (
            <div />
          )}
        </BasicModal>
      </div>
    </CandidatePoolTableContext.Provider>
  )
}

export const CandidatePool = memo(CandidatePoolComponent)
